import React, { useState, useEffect } from 'react';
import {
  InputAdornment,
  ThemeProvider,
  Box,
  CircularProgress,
} from '@mui/material';
import { Email, Person } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { getProfile, updateProfile } from '../DataService';
import { saveClaims } from '../CookieHelper';
import { theme } from '../styles/theme/theme';
import {
  AuthPageContainer,
  AuthFormCard,
  AuthFormContainer,
  AuthPageTitle,
  AuthSubtitle,
  AuthErrorMessage,
  AuthSuccessMessage,
  AuthTextField,
  AuthGradientButton,
} from '../styles/components';

const UpdateProfile: React.FC = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({ Name: '', Email: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idFromUrl = params.get('id');
    const idFromCookie = Cookies.get('UserId');

    let effectiveId: string | null = null;

    if (idFromUrl) {
      effectiveId = idFromUrl;
    } else if (idFromCookie) {
      effectiveId = idFromCookie;
    }

    if (effectiveId) {
      setUserId(effectiveId);
      const fetchProfile = async () => {
        try {
          const profileData = await getProfile(effectiveId!);
          setFormData(profileData.Claims);
        } catch (err: any) {
          setError(err?.message || 'Failed to load profile.');
        }
        setLoading(false);
      };
      fetchProfile();
    } else {
      setError('No user ID found in URL or session. Please sign in.');
      setLoading(false);
    }
  }, [location.search]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) {
      setError('User ID not found. Please sign in again.');
      return;
    }
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const res = await updateProfile(formData, userId);
      saveClaims(res.Claims);
      setSuccess('Profile updated successfully!');
    } catch (err: any) {
      setError(err?.message || 'Failed to update profile.');
    }
    setLoading(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <AuthPageContainer>
        <AuthFormCard elevation={6}>
          <AuthPageTitle variant="h5">Update Profile</AuthPageTitle>
          <AuthSubtitle variant="body2">Update your personal information.</AuthSubtitle>

          {loading ? (
            <CircularProgress sx={{ mt: 4 }} />
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <AuthFormContainer>
                <AuthTextField
                  required
                  fullWidth
                  id="name"
                  label="Full Name"
                  name="Name"
                  autoComplete="name"
                  value={formData.Name}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
                <AuthTextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="Email"
                  autoComplete="email"
                  value={formData.Email}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
                {error && <AuthErrorMessage>{error}</AuthErrorMessage>}
                {success && <AuthSuccessMessage>{success}</AuthSuccessMessage>}
                <AuthGradientButton type="submit" fullWidth variant="contained" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </AuthGradientButton>
              </AuthFormContainer>
            </Box>
          )}
        </AuthFormCard>
      </AuthPageContainer>
    </ThemeProvider>
  );
};

export default UpdateProfile; 